import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { getEstimates } from '../../../utils/estimateStorage';

const RiskHeatMap = () => {
  const [selectedCell, setSelectedCell] = useState(null);
  const allEstimates = getEstimates();

  const riskDimensions = [
    { id: 'technical', label: 'Technical Complexity', icon: 'Code' },
    { id: 'resource', label: 'Resource Availability', icon: 'Users' },
    { id: 'vendor', label: 'Vendor Dependency', icon: 'Package' },
    { id: 'timeline', label: 'Timeline Constraints', icon: 'Clock' }
  ];

  const calculateRiskScores = useMemo(() => {
    if (allEstimates?.length === 0) return [];

    return allEstimates?.slice(0, 20)?.map(estimate => {
      const calc = estimate?.calculations;
      
      // Technical Complexity Risk (based on cost structure)
      const engineerRatio = (calc?.totalEngineerCosts || 0) / (calc?.totalCost || 1);
      const licenseRatio = (calc?.totalLicensesCosts || 0) / (calc?.totalCost || 1);
      const technicalScore = Math.min(100, (
        (engineerRatio > 0.6 ? 30 : engineerRatio > 0.4 ? 15 : 5) +
        (licenseRatio > 0.2 ? 25 : licenseRatio > 0.1 ? 15 : 5) +
        (calc?.totalCost > 500000 ? 30 : calc?.totalCost > 200000 ? 20 : 10)
      ));

      // Resource Availability Risk (based on engineer count and hours)
      const engineerCount = estimate?.selectedEngineers?.reduce((sum, eng) => sum + (eng?.count || 0), 0) || 0;
      const totalHours = estimate?.selectedEngineers?.reduce((sum, eng) => sum + ((eng?.count || 0) * (eng?.hours || 0)), 0) || 0;
      const resourceScore = Math.min(100, (
        (engineerCount > 10 ? 35 : engineerCount > 5 ? 20 : 10) +
        (totalHours > 2000 ? 30 : totalHours > 1000 ? 15 : 5) +
        (estimate?.deliveryMode === 'onsite' ? 20 : estimate?.deliveryMode === 'hybrid' ? 10 : 5)
      ));

      // Vendor Dependency Risk (based on materials and licenses)
      const materialCount = estimate?.selectedMaterials?.length || 0;
      const licenseCount = estimate?.selectedLicenses?.length || 0;
      const vendorScore = Math.min(100, (
        (materialCount > 10 ? 30 : materialCount > 5 ? 15 : 5) +
        (licenseCount > 5 ? 25 : licenseCount > 2 ? 15 : 5) +
        ((calc?.totalMaterialsCost || 0) > 100000 ? 30 : (calc?.totalMaterialsCost || 0) > 50000 ? 15 : 5)
      ));

      // Timeline Constraints Risk (based on margin and contingency)
      const margin = calc?.profitMarginPercentage || 0;
      const contingency = ((calc?.contingencyCosts || 0) / (calc?.totalCost || 1)) * 100;
      const timelineScore = Math.min(100, (
        (margin < 15 ? 35 : margin < 20 ? 20 : 10) +
        (contingency < 5 ? 30 : contingency < 8 ? 15 : 5) +
        (calc?.totalCost > 300000 ? 25 : calc?.totalCost > 150000 ? 15 : 5)
      ));

      return {
        id: estimate?.id,
        name: estimate?.name || 'Unnamed Estimate',
        projectName: estimate?.projectName,
        timestamp: estimate?.timestamp,
        scores: {
          technical: technicalScore,
          resource: resourceScore,
          vendor: vendorScore,
          timeline: timelineScore
        },
        factors: {
          technical: [
            engineerRatio > 0.6 && 'High engineer cost ratio',
            licenseRatio > 0.2 && 'Significant license costs',
            calc?.totalCost > 500000 && 'Large project value'
          ]?.filter(Boolean),
          resource: [
            engineerCount > 10 && 'Large team size required',
            totalHours > 2000 && 'High total hours',
            estimate?.deliveryMode === 'onsite' && 'Onsite delivery constraints'
          ]?.filter(Boolean),
          vendor: [
            materialCount > 10 && 'Multiple material vendors',
            licenseCount > 5 && 'Multiple license dependencies',
            (calc?.totalMaterialsCost || 0) > 100000 && 'High material costs'
          ]?.filter(Boolean),
          timeline: [
            margin < 15 && 'Low profit margin buffer',
            contingency < 5 && 'Insufficient contingency',
            calc?.totalCost > 300000 && 'Complex project scope'
          ]?.filter(Boolean)
        }
      };
    });
  }, [allEstimates]);

  const getRiskColor = (score) => {
    if (score >= 70) return 'bg-error text-error-foreground';
    if (score >= 50) return 'bg-warning text-warning-foreground';
    if (score >= 30) return 'bg-accent text-accent-foreground';
    return 'bg-success text-success-foreground';
  };

  const getRiskLabel = (score) => {
    if (score >= 70) return 'High';
    if (score >= 50) return 'Medium-High';
    if (score >= 30) return 'Medium';
    return 'Low';
  };

  const handleCellClick = (estimate, dimension) => {
    setSelectedCell({
      estimate,
      dimension,
      score: estimate?.scores?.[dimension],
      factors: estimate?.factors?.[dimension]
    });
  };

  const handleCloseTooltip = () => {
    setSelectedCell(null);
  };

  if (calculateRiskScores?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="AlertTriangle" size={20} className="text-warning" />
          Risk Heat Map
        </h3>
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="Shield" size={48} className="text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No risk data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1 flex items-center gap-2">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
            Risk Heat Map
          </h3>
          <p className="text-sm text-muted-foreground">
            Multi-dimensional risk assessment across project portfolio
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-success"></div>
            <span className="text-muted-foreground">Low</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-accent"></div>
            <span className="text-muted-foreground">Medium</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-warning"></div>
            <span className="text-muted-foreground">Med-High</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-error"></div>
            <span className="text-muted-foreground">High</span>
          </div>
        </div>
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 text-sm font-semibold text-foreground border-b border-border min-w-[200px]">
                  Project / Estimate
                </th>
                {riskDimensions?.map(dim => (
                  <th key={dim?.id} className="text-center p-3 text-sm font-semibold text-foreground border-b border-border">
                    <div className="flex flex-col items-center gap-1">
                      <Icon name={dim?.icon} size={16} />
                      <span className="whitespace-nowrap">{dim?.label}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {calculateRiskScores?.map((estimate, idx) => (
                <tr key={estimate?.id} className="hover:bg-muted/30 transition-colors">
                  <td className="p-3 border-b border-border">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-medium text-foreground truncate max-w-[180px]">
                        {estimate?.name}
                      </p>
                      {estimate?.projectName && (
                        <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                          {estimate?.projectName}
                        </p>
                      )}
                    </div>
                  </td>
                  {riskDimensions?.map(dim => {
                    const score = estimate?.scores?.[dim?.id];
                    return (
                      <td key={dim?.id} className="p-3 text-center border-b border-border">
                        <button
                          onClick={() => handleCellClick(estimate, dim?.id)}
                          className={`inline-flex items-center justify-center px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-smooth hover:scale-105 hover:shadow-md ${getRiskColor(score)}`}
                          title="Click for details"
                        >
                          {score?.toFixed(0)}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Risk Detail Tooltip/Modal */}
      {selectedCell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-fade-in" onClick={handleCloseTooltip}>
          <div className="bg-card border border-border rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 animate-scale-in" onClick={(e) => e?.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${getRiskColor(selectedCell?.score)}`}>
                  <span className="text-xl font-bold">{selectedCell?.score?.toFixed(0)}</span>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-foreground">
                    {riskDimensions?.find(d => d?.id === selectedCell?.dimension)?.label}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Risk Level: {getRiskLabel(selectedCell?.score)}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseTooltip}
                className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium text-foreground mb-1">
                {selectedCell?.estimate?.name}
              </p>
              {selectedCell?.estimate?.projectName && (
                <p className="text-xs text-muted-foreground">
                  {selectedCell?.estimate?.projectName}
                </p>
              )}
            </div>

            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Risk Factors:</p>
              {selectedCell?.factors?.length > 0 ? (
                <ul className="space-y-2">
                  {selectedCell?.factors?.map((factor, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Icon name="AlertCircle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No specific risk factors identified</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskHeatMap;